import { useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { CONTACT, NAV, SOCIALS, whatsappLink } from "@/data/content";
import { FOOTER_DISCLAIMER } from "@/data/legal";
import { GUIDE_INDEX } from "@/data/guideIndex";
import { useQuiz } from "@/context/QuizContext";
import { useEnquiry } from "@/context/EnquiryContext";
import "./Footer.css";

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const EXPLORE = [
  ...NAV.map((n) => ({ label: n.label, to: n.path, internal: true })),
  { label: "Careers", to: "/careers", internal: true, testid: "footer-careers-link" },
  { label: "Growth platform", to: "/platform", internal: true },
  { label: "Breakthrough stories", to: "/careers#stories", internal: true },
  { label: "News & insights", href: "https://affinitygroup.sg/news/", internal: false },
  { label: "Core Team portal", to: "/admin/login", internal: true, testid: "footer-admin-link" },
  { label: "Surge Engine", to: "/surge/login", internal: true, testid: "footer-surge-link" },
  { label: "Nexus (team app)", to: "/nexus/login", internal: true, testid: "footer-nexus-link" },
];

const CAREER_PATHS = ["/careers", "/platform", "/join"];

export const Footer = () => {
  const { openQuiz } = useQuiz();
  const { openEnquiry } = useEnquiry();
  const { pathname } = useLocation();
  const isCareer = CAREER_PATHS.some((p) => pathname.startsWith(p));
  const footerRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    const footer = footerRef.current;
    if (!footer) return;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d", { alpha: true });
    let width = 0, height = 0, dpr = 1, active = true, frame = 0;
    const pointer = { x: -9999, y: -9999, active: false };
    let dots = [];

    const resize = () => {
      if (!canvas || !ctx) return;
      const rect = footer.getBoundingClientRect();
      width = Math.max(1, rect.width);
      height = Math.max(1, rect.height);
      dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      const count = window.innerWidth < 680 ? 18 : Math.min(44, Math.max(26, Math.round((width * Math.min(height, 900)) / 34000)));
      dots = Array.from({ length: count }, (_, index) => dots[index] || ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.24,
        vy: (Math.random() - 0.5) * 0.24,
        radius: 0.8 + Math.random() * 1.45,
        phase: Math.random() * Math.PI * 2,
      }));
    };

    const draw = (time) => {
      if (!ctx || reducedMotion) return;
      if (active) {
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, width, height);
        dots.forEach((dot) => {
          dot.x += dot.vx;
          dot.y += dot.vy;
          if (dot.x < -10) dot.x = width + 10;
          if (dot.x > width + 10) dot.x = -10;
          if (dot.y < -10) dot.y = height + 10;
          if (dot.y > height + 10) dot.y = -10;
          const alpha = 0.28 + Math.sin(time * 0.0011 + dot.phase) * 0.13;
          ctx.beginPath();
          ctx.arc(dot.x, dot.y, dot.radius, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(212,168,77,${alpha})`;
          ctx.fill();
        });
        const limit = window.innerWidth < 680 ? 85 : 120;
        for (let i = 0; i < dots.length; i++) {
          for (let j = i + 1; j < dots.length; j++) {
            const a = dots[i], b = dots[j];
            const distance = Math.hypot(a.x - b.x, a.y - b.y);
            if (distance < limit) {
              ctx.beginPath();
              ctx.moveTo(a.x, a.y);
              ctx.lineTo(b.x, b.y);
              ctx.strokeStyle = `rgba(212,168,77,${(1 - distance / limit) * 0.12})`;
              ctx.lineWidth = 0.7;
              ctx.stroke();
            }
          }
          if (pointer.active) {
            const dot = dots[i];
            const distance = Math.hypot(dot.x - pointer.x, dot.y - pointer.y);
            if (distance < 180) {
              ctx.beginPath();
              ctx.moveTo(dot.x, dot.y);
              ctx.lineTo(pointer.x, pointer.y);
              ctx.strokeStyle = `rgba(242,216,142,${(1 - distance / 180) * 0.32})`;
              ctx.stroke();
            }
          }
        }
      }
      frame = requestAnimationFrame(draw);
    };

    const onPointerMove = (event) => {
      const rect = footer.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      footer.style.setProperty("--aff-mx", `${x}px`);
      footer.style.setProperty("--aff-my", `${y}px`);
      pointer.x = x;
      pointer.y = y;
      pointer.active = true;
    };
    const onPointerLeave = () => {
      footer.style.setProperty("--aff-mx", "50%");
      footer.style.setProperty("--aff-my", "50%");
      pointer.active = false;
    };
    footer.addEventListener("pointermove", onPointerMove, { passive: true });
    footer.addEventListener("pointerleave", onPointerLeave);

    const visibilityObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => { active = entry.isIntersecting; });
    }, { threshold: 0.01 });
    visibilityObserver.observe(footer);

    const revealObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("is-visible");
          revealObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12 });
    footer.querySelectorAll(".aff-footer__reveal").forEach((el) => revealObserver.observe(el));

    let resizeObserver;
    if (!reducedMotion && canvas && ctx) {
      resizeObserver = new ResizeObserver(resize);
      resizeObserver.observe(footer);
      resize();
      frame = requestAnimationFrame(draw);
    }

    return () => {
      cancelAnimationFrame(frame);
      footer.removeEventListener("pointermove", onPointerMove);
      footer.removeEventListener("pointerleave", onPointerLeave);
      visibilityObserver.disconnect();
      revealObserver.disconnect();
      resizeObserver?.disconnect();
    };
  }, []);

  const scrollTop = () => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({ top: 0, behavior: reduced ? "auto" : "smooth" });
  };

  return (
    <footer ref={footerRef} className="aff-footer" data-testid="site-footer">
      <canvas ref={canvasRef} className="aff-footer__canvas" aria-hidden="true" />
      <div className="aff-footer__grain" aria-hidden="true" />
      <div className="aff-footer__grid" aria-hidden="true" />
      <div className="aff-footer__orb" aria-hidden="true" />
      <div className="aff-footer__watermark" aria-hidden="true">AFFINITY</div>
      <div className="aff-footer__inner">
        <section className="aff-footer__cta aff-footer__reveal" data-testid="final-cta-section" aria-labelledby="aff-footer-title">
          {isCareer ? (
            <>
              <div>
                <p className="aff-footer__eyebrow">Your next chapter</p>
                <h2 id="aff-footer-title">You do not have to build your<br /><em>breakthrough alone.</em></h2>
                <p>Explore whether Affinity's culture, systems and growth platform could help you build your advisory career with greater direction.</p>
              </div>
              <div className="aff-footer__actions">
                <button type="button" className="aff-footer__button aff-footer__button--primary" data-testid="final-fit-check-button" onClick={() => openQuiz("footer-cta")}>Explore your fit <span>&rarr;</span></button>
                <Link className="aff-footer__button aff-footer__button--secondary" data-testid="final-speak-privately" to="/careers#conversation">Speak privately <span>&#8599;</span></Link>
              </div>
            </>
          ) : (
            <>
              <div>
                <p className="aff-footer__eyebrow">Start a conversation</p>
                <h2 id="aff-footer-title">Good advice begins with<br /><em>someone who listens.</em></h2>
                <p>Whether you are protecting a young family, planning retirement or thinking about your business's next chapter, the first step is a relaxed, no-obligation conversation.</p>
              </div>
              <div className="aff-footer__actions">
                <button type="button" className="aff-footer__button aff-footer__button--primary" data-testid="final-enquire-button" onClick={() => openEnquiry("advisor")}>Talk to an advisor <span>&rarr;</span></button>
                <Link className="aff-footer__button aff-footer__button--secondary" data-testid="final-careers-link" to="/careers">Careers at Affinity <span>&#8599;</span></Link>
              </div>
            </>
          )}
        </section>

        <div className="aff-footer__main aff-footer__reveal">
          <div className="aff-footer__brand-column">
            <Link to="/" className="aff-footer__brand" data-testid="brand-link" aria-label="Affinity Group home">
              <img className="aff-footer__logo-img" src="/img/affinity-logo.png" alt="Affinity Group" loading="lazy" decoding="async" />
            </Link>
            <p className="aff-footer__brand-copy">Affinity Group is a group of Financial Adviser Representatives representing Infinity Financial Advisory Pte. Ltd. We build culture, systems, visibility, opportunity, technology and leadership around advisors who want to grow seriously.</p>
            <p className="aff-footer__quote">"Your ambition deserves more than a job. It deserves the right platform, the right people, and a future worth building."</p>
          </div>

          <nav aria-label="Footer navigation">
            <h3 className="aff-footer__heading">Explore</h3>
            <div className="aff-footer__links">
              {EXPLORE.map((item) =>
                item.internal ? (
                  <Link key={item.label} className="aff-footer__link aff-footer__link--internal" to={item.to} data-testid={item.testid || `footer-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}>{item.label}</Link>
                ) : (
                  <a key={item.label} className="aff-footer__link" href={item.href} target="_blank" rel="noopener noreferrer">{item.label}</a>
                )
              )}
            </div>
          </nav>

          <nav aria-label="Affinity social platforms">
            <h3 className="aff-footer__heading">Follow</h3>
            <div className="aff-footer__links">
              {SOCIALS.map((s, i) => (
                <a key={s.name} className="aff-footer__social-link" href={s.href} target="_blank" rel="noopener noreferrer" data-testid={`footer-social-${s.name.toLowerCase()}`}>
                  <span className="aff-footer__social-number">0{i + 1}</span>
                  <span>{s.name}</span>
                  <span className="aff-footer__social-arrow">&#8599;</span>
                </a>
              ))}
            </div>
          </nav>

          <div className="aff-footer__visit-column">
            <h3 className="aff-footer__heading">Visit</h3>
            <address className="aff-footer__address">10 Eunos Road 8<br />#08-02, #14-03<br />Singapore 408600</address>
            <div className="aff-footer__contact">
              <a href="https://www.google.com/maps/search/?api=1&query=10%20Eunos%20Road%208%20Singapore%20408600" target="_blank" rel="noopener noreferrer"><span className="aff-footer__contact-icon" aria-hidden="true">&#9678;</span><span>Open in Google Maps</span></a>
              <a href={`mailto:${CONTACT.email}`}><span className="aff-footer__contact-icon" aria-hidden="true">@</span><span>{CONTACT.email}</span></a>
            </div>
            {isCareer ? (
              <a className="aff-footer__whatsapp" href={whatsappLink()} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp-link">
                <span className="aff-footer__whatsapp-dot" aria-hidden="true"><WhatsAppIcon /></span>
                <span>WhatsApp {CONTACT.whatsappDisplay}</span>
              </a>
            ) : (
              <button type="button" className="aff-footer__whatsapp" onClick={() => openEnquiry("advisor")} data-testid="footer-enquire-link">
                <span className="aff-footer__whatsapp-dot" aria-hidden="true"><WhatsAppIcon /></span>
                <span>Send us an enquiry</span>
              </button>
            )}
          </div>
        </div>

        <nav className="aff-footer__guides aff-footer__reveal" aria-label="Planning guides" data-testid="footer-guides">
          <h3 className="aff-footer__heading">Planning &amp; career guides</h3>
          <div className="aff-footer__pills">
            {GUIDE_INDEX.map((g) => (
              <Link key={g.slug} className="aff-footer__pill" to={`/${g.slug}`} data-testid={`footer-guide-${g.slug}`}>{g.nav}</Link>
            ))}
            <Link className="aff-footer__pill aff-footer__pill--all" to="/guides" data-testid="footer-guides-all">All guides &rarr;</Link>
          </div>
        </nav>

        <div className="aff-footer__disclaimer" data-testid="footer-disclaimer">
          {FOOTER_DISCLAIMER.map((p, i) => (
            <p key={i}>{i === 0 && <strong>Disclaimer: </strong>}{p}</p>
          ))}
          <Link to="/disclaimer" className="aff-footer__disclaimer-more" data-testid="footer-disclaimer-more">Read the full disclaimer &rarr;</Link>
        </div>

        <div className="aff-footer__bottom">
          <div>
          <p className="aff-footer__fine" data-testid="footer-fine">© {new Date().getFullYear()} Affinity Group · A group of Financial Adviser Representatives representing Infinity Financial Advisory Pte. Ltd. Career opportunities and appointments are subject to applicable regulatory requirements and suitability. Past outcomes are not a guarantee of individual results.</p>
          <nav className="aff-footer__legal" aria-label="Legal"><Link to="/disclaimer" data-testid="footer-disclaimer-link">Disclaimer</Link><Link to="/privacy-policy" data-testid="footer-privacy-link">Privacy policy</Link></nav>
          </div>
          <button className="aff-footer__top" type="button" aria-label="Back to top" onClick={scrollTop}>&#8593;</button>
        </div>
      </div>
    </footer>
  );
};
